import { extendTheme } from "@chakra-ui/react";
import { glassFieldSx } from "./glassSx";

/**
 * White translucent liquid-glass surfaces over the shared photo canvas.
 * Text on glass is dark ink, so it must always live inside one of these
 * surfaces: bare text over the photo background is unreadable.
 */
export const glassSurfaces = {
  subtle: "rgba(255, 255, 255, 0.38)",
  default: "rgba(255, 255, 255, 0.58)",
  strong: "rgba(255, 255, 255, 0.74)",
  solid: "rgba(255, 255, 255, 0.92)",
  nav: "rgba(11, 17, 32, 0.82)",
  field: "rgba(255, 255, 255, 0.78)",
};

const theme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
  colors: {
    brand: {
      50: "#f3f9e8",
      100: "#e2f0c4",
      200: "#c9e392",
      300: "#b4d96b",
      400: "#9fcf5e",
      500: "#9bc04d",
      600: "#7a9c35",
      700: "#5c7827",
      800: "#1f1e30",
      900: "#141320",
      901: "#95f05e",
      body: "#e0ebfc",
    },
    glass: glassSurfaces,
    text: {
      /** Glass typography: dark ink over the white translucent scrim. */
      onGlass: "#2d3748",
      onGlassMuted: "#4a5568",
      onGlassSubtle: "#718096",
      /** Solid (admin) contexts. */
      solid: "#1a202c",
      solidMuted: "#4a5568",
    },
  },
  fonts: {
    heading: "Okomito, sans-serif",
    body: "Okomito, sans-serif",
  },
  radii: {
    control: "14px",
    surface: "22px",
    modal: "26px",
    pill: "999px",
  },
  shadows: {
    glass:
      "0 6px 18px rgba(2, 6, 23, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.58)",
    glassHover:
      "0 10px 26px rgba(2, 6, 23, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
    overlay: "0 26px 64px rgba(2, 6, 23, 0.55)",
    focus: "0 0 0 3px rgba(155, 192, 77, 0.75)",
  },
  styles: {
    global: {
      "html, body": {
        minHeight: "100%",
        bg: "brand.body",
        color: "text.solid",
      },
      "*:focus-visible": {
        outline: "3px solid",
        outlineColor: "brand.400",
        outlineOffset: "2px",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          transitionDuration: "0.01ms !important",
          scrollBehavior: "auto !important",
        },
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: "semibold",
        borderRadius: "control",
        _focusVisible: { boxShadow: "focus" },
      },
    },
    Modal: {
      baseStyle: {
        dialog: { borderRadius: "modal" },
      },
    },
    /** Opt-in variants for glass contexts; admin keeps the Chakra defaults. */
    Table: {
      variants: {
        glass: {
          table: { color: "gray.700" },
          thead: {
            th: {
              color: "gray.600",
              borderColor: "blackAlpha.200",
              bg: "whiteAlpha.500",
              textTransform: "uppercase",
              fontSize: "xs",
              letterSpacing: "0.06em",
            },
          },
          tbody: {
            tr: {
              transition: "background 0.15s ease",
              _odd: { bg: "whiteAlpha.300" },
              _hover: { bg: "whiteAlpha.700" },
            },
            td: {
              borderColor: "blackAlpha.100",
              color: "gray.700",
            },
          },
          tfoot: {
            th: { color: "gray.600", borderColor: "blackAlpha.200" },
          },
        },
      },
    },
    /** Floating layers live outside the glass canvas, so they carry it. */
    Menu: {
      baseStyle: {
        list: {
          bg: "glass.solid",
          color: "gray.700",
          borderColor: "blackAlpha.200",
          borderRadius: "control",
          backdropFilter: "blur(22px) saturate(150%)",
          boxShadow: "overlay",
          py: 2,
        },
        item: {
          bg: "transparent",
          color: "gray.700",
          _hover: { bg: "blackAlpha.50" },
          _focus: { bg: "blackAlpha.50" },
        },
        divider: { borderColor: "blackAlpha.200" },
      },
    },
    Popover: {
      baseStyle: {
        content: {
          bg: "glass.solid",
          color: "gray.700",
          borderColor: "blackAlpha.200",
          borderRadius: "control",
          backdropFilter: "blur(22px) saturate(150%)",
          boxShadow: "overlay",
          "--popper-arrow-bg": "rgba(255, 255, 255, 0.92)",
          _focusVisible: { outline: "none", boxShadow: "overlay" },
          ...glassFieldSx,
        },
        closeButton: { color: "gray.600" },
      },
    },
    Alert: {
      variants: {
        glass: (props) => ({
          container: {
            bg: "rgba(255, 255, 255, 0.78)",
            color: "gray.700",
            borderRadius: "control",
            borderLeft: "4px solid",
            borderColor: `${props.colorScheme}.400`,
            backdropFilter: "blur(14px)",
          },
          icon: { color: `${props.colorScheme}.500` },
          title: { color: "gray.800" },
          description: { color: "gray.600" },
        }),
      },
    },
  },
});

export default theme;
